/**
 * La tabla del ERP.
 *
 * ERP-UX.md §3 la llama «el componente más importante del ERP», y lo es:
 * casi todas las pantallas son una tabla con distinto contenido.
 *
 * **Lo que esta tabla NO hace, y por qué.** La API de listados pagina por
 * cursor, filtra por búsqueda y estado, y ordena por `id`. No ordena por
 * columna, no filtra por columna y no devuelve total. Así que aquí:
 *
 * - Sólo se ofrece ordenar por las columnas que el origen declara
 *   ordenables. Ordenar en el navegador las 200 filas cargadas y llamarlo
 *   «ordenado por importe» sería mentir sobre las 3.000 que no están.
 * - El recuento sale como «cargadas», nunca como total.
 *
 * Cuando la API crezca, la tabla ya sabe pedirlo: el estado vive en la URL
 * y viaja al servidor tal cual.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { computeWindow, shouldLoadMore } from './virtual-window.js';
import {
  EMPTY_SELECTION,
  clearSelection,
  isSelected,
  resolveIds,
  selectAllMatching,
  summarize,
  toggleAllVisible,
  toggleRow,
  type Selection,
} from './selection.js';
import { describeFilters, isFiltered, type TableState } from './table-state.js';
import { downloadCsv, toCsv, type CsvColumn } from './csv.js';

export interface Column<Row> {
  id: string;
  label: string;
  /** Contenido de la celda. */
  render: (row: Row) => React.ReactNode;
  /** Qué se exporta. Por defecto, lo mismo que se pinta si es texto. */
  exportValue?: (row: Row) => unknown;
  /** ¿El SERVIDOR sabe ordenar por esta columna? */
  sortable?: boolean;
  width?: string;
  /** Números y fechas se alinean a la derecha y con cifras tabulares. */
  numeric?: boolean;
}

export interface DataTableProps<Row> {
  columns: readonly Column<Row>[];
  rows: readonly Row[];
  idOf: (row: Row) => string;
  state: TableState;
  onStateChange: (state: TableState) => void;
  /** Nombre del fichero exportado, sin extensión. */
  exportName: string;
  loading?: boolean;
  /** Hay más páginas por cargar. */
  hasMore?: boolean;
  onLoadMore?: () => void;
  /** Total del servidor, si algún día lo hay. */
  totalMatching?: number | null;
  emptyMessage?: string;
  onOpenRow?: (row: Row) => void;
  /**
   * Acciones sobre lo seleccionado.
   *
   * Recibe los identificadores elegidos, o `null` cuando la selección es
   * «todas las que cumplen el filtro» — en ese caso el cliente no los
   * conoce, sólo el servidor, y la acción debe decidir si puede con ello o
   * se desactiva. Devolver ids a medias sería peor: la acción se aplicaría
   * en silencio sobre un subconjunto.
   */
  bulkActions?: (ids: readonly string[] | null) => React.ReactNode;
}

const ROW_HEIGHT = { compact: 32, comfortable: 44 } as const;

export function DataTable<Row>({
  columns,
  rows,
  idOf,
  state,
  onStateChange,
  exportName,
  loading = false,
  hasMore = false,
  onLoadMore,
  totalMatching = null,
  emptyMessage = 'No hay nada que enseñar todavía.',
  onOpenRow,
  bulkActions,
}: DataTableProps<Row>): React.ReactNode {
  const [selection, setSelection] = useState<Selection>(EMPTY_SELECTION);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(600);
  const viewport = useRef<HTMLDivElement>(null);

  const visible = useMemo(
    () => columns.filter((c) => !state.hiddenColumns.includes(c.id)),
    [columns, state.hiddenColumns],
  );

  const ids = useMemo(() => rows.map(idOf), [rows, idOf]);
  const rowHeight = ROW_HEIGHT[state.density];

  useEffect(() => {
    const element = viewport.current;
    if (!element) return;

    const observer = new ResizeObserver(() => setViewportHeight(element.clientHeight));
    observer.observe(element);
    setViewportHeight(element.clientHeight);
    return () => observer.disconnect();
  }, []);

  const window_ = computeWindow({
    totalRows: rows.length,
    rowHeight,
    viewportHeight,
    scrollTop,
  });

  const onScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const element = event.currentTarget;
      setScrollTop(element.scrollTop);

      if (
        hasMore &&
        !loading &&
        onLoadMore &&
        shouldLoadMore({
          scrollTop: element.scrollTop,
          viewportHeight: element.clientHeight,
          contentHeight: element.scrollHeight,
        })
      ) {
        onLoadMore();
      }
    },
    [hasMore, loading, onLoadMore],
  );

  function toggleSort(column: Column<Row>): void {
    if (!column.sortable) return;

    const current = state.sort;
    const direction = current?.column === column.id && current.direction === 'asc' ? 'desc' : 'asc';
    onStateChange({ ...state, sort: { column: column.id, direction } });
  }

  function toggleColumn(id: string): void {
    const hidden = state.hiddenColumns.includes(id)
      ? state.hiddenColumns.filter((c) => c !== id)
      : [...state.hiddenColumns, id];
    onStateChange({ ...state, hiddenColumns: hidden.sort() });
  }

  function exportCsv(): void {
    const chosen = resolveIds(selection, ids);
    // Si hay selección, se exporta la selección; si no, lo cargado. En
    // ningún caso se exporta lo que no está en pantalla sin decirlo.
    const subset =
      chosen && chosen.length > 0 ? rows.filter((r) => chosen.includes(idOf(r))) : rows;

    const csvColumns: CsvColumn<Row>[] = visible.map((c) => ({
      id: c.id,
      label: c.label,
      value: c.exportValue ?? ((row: Row) => textOf(c.render(row))),
    }));

    downloadCsv(exportName, toCsv(csvColumns, subset));
  }

  const summary = summarize(selection, totalMatching);
  const selectionActive = selection.mode === 'ALL_MATCHING' || selection.ids.size > 0;
  const allVisibleSelected = ids.length > 0 && ids.every((id) => isSelected(selection, id));
  const activeFilters = describeFilters(state, (id) => columns.find((c) => c.id === id)?.label ?? id);

  return (
    <div className="table">
      <div className="table__bar">
        <input
          className="table__search"
          type="search"
          placeholder="Buscar…"
          defaultValue={state.search}
          onChange={(e) => onStateChange({ ...state, search: e.target.value })}
          aria-label="Buscar en la tabla"
        />

        <div className="table__bar-spacer" />

        <select
          className="table__density"
          value={state.density}
          onChange={(e) =>
            onStateChange({ ...state, density: e.target.value === 'comfortable' ? 'comfortable' : 'compact' })
          }
          aria-label="Densidad de las filas"
        >
          <option value="compact">Compacta</option>
          <option value="comfortable">Cómoda</option>
        </select>

        <details className="table__columns">
          <summary>Columnas</summary>
          <div className="table__columns-menu">
            {columns.map((column) => (
              <label key={column.id}>
                <input
                  type="checkbox"
                  checked={!state.hiddenColumns.includes(column.id)}
                  onChange={() => toggleColumn(column.id)}
                />
                {column.label}
              </label>
            ))}
          </div>
        </details>

        <button className="button button--quiet" type="button" onClick={exportCsv}>
          Exportar
        </button>
      </div>

      {activeFilters.length > 0 && (
        <div className="table__filters" role="status">
          {/* Se dice en voz alta porque el fallo caro de una tabla filtrada
              no es filtrar mal: es olvidar que está filtrada. */}
          <span>Filtrado por: {activeFilters.join(' · ')}</span>
          {isFiltered(state) && (
            <button
              className="table__clear"
              type="button"
              onClick={() => onStateChange({ ...state, search: '', filters: {} })}
            >
              Quitar filtros
            </button>
          )}
        </div>
      )}

      {selectionActive && (
        <div className="table__selection" role="status">
          <span>{summary.label}</span>

          {selection.mode === 'ROWS' && allVisibleSelected && hasMore && (
            <button className="table__link" type="button" onClick={() => setSelection(selectAllMatching())}>
              Seleccionar todas las que cumplen el filtro
            </button>
          )}

          {summary.needsCount && (
            <span className="table__warn">
              ⚠ El servidor todavía no devuelve el total: las acciones masivas sobre «todas» están
              desactivadas hasta saber sobre cuántas filas actuarían.
            </span>
          )}

          {bulkActions?.(resolveIds(selection, ids))}

          <button className="table__link" type="button" onClick={() => setSelection(clearSelection())}>
            Quitar selección
          </button>
        </div>
      )}

      <div className="table__viewport" ref={viewport} onScroll={onScroll}>
        <table className={`table__grid table__grid--${state.density}`}>
          <thead>
            <tr>
              <th className="table__check">
                <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  onChange={() => setSelection((s) => toggleAllVisible(s, ids))}
                  aria-label="Seleccionar todo lo cargado"
                />
              </th>
              {visible.map((column) => (
                <th
                  key={column.id}
                  style={column.width ? { width: column.width } : undefined}
                  className={column.numeric ? 'table__num' : undefined}
                  aria-sort={
                    state.sort?.column === column.id
                      ? state.sort.direction === 'asc'
                        ? 'ascending'
                        : 'descending'
                      : undefined
                  }
                >
                  {column.sortable ? (
                    <button className="table__sort" type="button" onClick={() => toggleSort(column)}>
                      {column.label}
                      <span aria-hidden="true">
                        {state.sort?.column === column.id
                          ? state.sort.direction === 'asc'
                            ? ' ↑'
                            : ' ↓'
                          : ''}
                      </span>
                    </button>
                  ) : (
                    column.label
                  )}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {window_.paddingTop > 0 && (
              <tr style={{ height: window_.paddingTop }} aria-hidden="true">
                <td colSpan={visible.length + 1} />
              </tr>
            )}

            {rows.slice(window_.start, window_.end).map((row) => {
              const id = idOf(row);
              return (
                <tr
                  key={id}
                  className={isSelected(selection, id) ? 'table__row table__row--on' : 'table__row'}
                  onClick={() => onOpenRow?.(row)}
                >
                  <td className="table__check" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={isSelected(selection, id)}
                      onChange={() => undefined}
                      onClick={(e) => setSelection((s) => toggleRow(s, id, ids, e.shiftKey))}
                      aria-label={`Seleccionar fila ${id}`}
                    />
                  </td>
                  {visible.map((column) => (
                    <td key={column.id} className={column.numeric ? 'table__num' : undefined}>
                      {column.render(row)}
                    </td>
                  ))}
                </tr>
              );
            })}

            {window_.paddingBottom > 0 && (
              <tr style={{ height: window_.paddingBottom }} aria-hidden="true">
                <td colSpan={visible.length + 1} />
              </tr>
            )}
          </tbody>
        </table>

        {rows.length === 0 && !loading && <p className="table__empty">{emptyMessage}</p>}
        {loading && <p className="table__loading">Cargando…</p>}
      </div>

      <footer className="table__foot">
        {/* «Cargadas», no «total»: el servidor no da un total y ponerlo
            haría creer que la lista se acaba ahí. */}
        {rows.length} {rows.length === 1 ? 'fila cargada' : 'filas cargadas'}
        {hasMore && ' · hay más'}
      </footer>
    </div>
  );
}

/** Texto plano de una celda, para exportar lo que se ve. */
function textOf(node: React.ReactNode): string {
  if (node === null || node === undefined || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(textOf).join('');
  // Un elemento de React sin `exportValue` no se puede leer sin renderizar:
  // la columna que lo necesite tiene que declarar el suyo.
  return '';
}
